<template>
  <div
    class="bet-chip-selector"
    :class="{ 'bet-chip-selector--fancy': fancy }"
    :aria-label="$t('Bet chips')"
  >
    <button
      v-for="(chip, index) in chips"
      :key="chip"
      type="button"
      class="bet-chip-selector__option"
      :class="{ 'bet-chip-selector__option--active': isChipActive(chip) }"
      @click="$emit('select-chip', chip)"
    >
      <Chip class="bet-chip-selector__chip" :chip-value="chip" display-only>
        <ChipDisplay :chip="chipColors[index]" />
      </Chip>
    </button>

    <CustomChip
      v-if="custom"
      class="bet-chip-selector__option bet-chip-selector__custom"
      display-only
      :active-chip="activeChip"
      :arena-id="arenaId"
      @select-chip="$emit('select-chip', $event)"
    />
  </div>
</template>

<script>
import Chip from '@/components/bet-panel/chip';
import ChipDisplay from '@/components/bet-panel/chip/chip-display.vue';
import CustomChip from '@/components/bet-panel/chip/custom-chip.vue';

export default {
  components: {
    Chip,
    ChipDisplay,
    CustomChip,
  },
  props: {
    chips: {
      type: Array,
      required: true,
    },
    activeChip: {
      type: Number,
      required: true,
    },
    fancy: {
      type: Boolean,
      default: true,
    },
    chipColors: {
      type: Array,
      default: () => ['blue', 'orange', 'black', 'purple', 'yellow'],
    },
    custom: {
      type: Boolean,
      default: false,
    },
    // The table's arena, so the custom chip saves against it
    // (PATCH /arenas/:id/bet-chips).
    arenaId: {
      type: String,
      default: '',
    },
  },
  methods: {
    isChipActive(chip) {
      return Number(this.activeChip) === Number(chip);
    },
  },
};
</script>

<style lang="scss" scoped>
.bet-chip-selector {
  display: flex;
  flex-wrap: nowrap;
  justify-content: flex-start;
  align-items: center;
  gap: 2px;

  &--fancy {
    width: 100%;
    justify-content: space-between;
    gap: 4px;
    overflow-x: visible;
  }
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
}

.bet-chip-selector__option {
  flex: 0 0 36px;
  width: 36px;
  height: 36px;
  min-height: 36px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

/*
 * The chip itself is the cockfight Chip component, so its look -- art, centred
 * value, text shadow, ring -- comes from `.chip-wrapper`. Only the footprint is
 * overridden: `.chip-wrapper` is built for the 58px+ cockfight panel, which
 * does not fit between Undo and Bet on a Tài Xỉu bar. These are plain selectors
 * because a child component's root carries this component's scope id too, and
 * naming both classes keeps them ahead of `.chip-wrapper`'s own rules.
 */
.bet-chip-selector__chip.chip-wrapper,
.bet-chip-selector__chip.chip-wrapper.displayOnly {
  width: 100%;
  height: 100%;
  min-width: 0;
  min-height: 0;
  max-width: none;
  flex: 1 1 auto;
  padding: 0;
}

/*
 * The amount field stays anchored to the actions row, which is the one ancestor
 * here that does not clip -- `.bet-chip-selector` is a horizontal scroller, so
 * anchoring to the chip itself put the field inside that clip and it vanished.
 * Its look over that row lives with the row, in bet-board-actions.vue.
 */
.bet-chip-selector__custom {

  ::v-deep .custom-chip.chip-wrapper {
    width: 100%;
    height: 100%;
    min-width: 0;
    min-height: 0;
    max-width: none;
    padding: 0;
  }

  // No room under a 36px chip for the cockfight panel's label.
  ::v-deep .custom-chip-label {
    display: none;
  }
}

/*
 * `display-only` keeps the Chip from reading the cockfight store's active chip,
 * so the Tài Xỉu selection drives the ring from here, with the same values
 * `.chip-wrapper.active` uses.
 */
.bet-chip-selector__option--active .bet-chip-selector__chip.chip-wrapper {
  border: 1px solid rgba(255, 255, 255, 0.8);
  box-shadow: 0 0 6px 0 #fff;
}

</style>
